import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';

const PipelineDefinitionForm = () => {
  const [pipelines, setPipelines] = useState([]);
  const [newPipeline, setNewPipeline] = useState({ name: '', description: '', isActive: true });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPipeline, setEditingPipeline] = useState(null);

  // Pipelines abrufen und sortieren
  useEffect(() => {
    const fetchPipelines = async () => {
      try {
        const response = await api.get('/pipeline-definitions');
        const sorted = response.data.sort((a, b) => a.name.localeCompare(b.name));
        setPipelines(sorted);
      } catch (err) {
        setError('Fehler beim Abrufen der Pipelines');
      } finally {
        setLoading(false);
      }
    };
    fetchPipelines();
  }, []);

  // Neue Pipeline anlegen
  const handleAddPipeline = async () => {
    const { name, description, isActive } = newPipeline;
    if (!name) {
      alert('Bitte den Namen der Pipeline angeben.');
      return;
    }
    try {
      const response = await api.post('/pipeline-definitions', { name, description, isActive });
      const updatedList = [...pipelines, response.data].sort((a, b) => a.name.localeCompare(b.name));
      setPipelines(updatedList);
      setNewPipeline({ name: '', description: '', isActive: true });
    } catch (err) {
      alert('Fehler beim Hinzufügen der Pipeline');
    }
  };

  // Pipeline löschen
  const handleDeletePipeline = async (id) => {
    if (!window.confirm('Möchten Sie diese Pipeline wirklich löschen?')) return;
    try {
      await api.delete(`/pipeline-definitions/${id}`);
      setPipelines(pipelines.filter(p => p.id !== id));
    } catch (err) {
      alert('Fehler beim Löschen der Pipeline');
    }
  };

  // Bearbeiten starten
  const handleEditClick = (pipeline) => {
    setEditingPipeline({ ...pipeline });
  };

  // Bearbeitung abbrechen
  const handleCancelEdit = () => {
    setEditingPipeline(null);
  };

  // Änderungen bei Eingaben übernehmen
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingPipeline(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Speichern der Änderungen
  const handleSavePipeline = async () => {
    const { id, name, description, isActive } = editingPipeline;
    if (!name) {
      alert('Bitte den Namen der Pipeline angeben.');
      return;
    }
    try {
      const response = await api.put(`/pipeline-definitions/${id}`, { name, description, isActive });
      const updatedList = pipelines.map(p => p.id === id ? response.data : p)
        .sort((a, b) => a.name.localeCompare(b.name));
      setPipelines(updatedList);
      setEditingPipeline(null);
    } catch (err) {
      alert('Fehler beim Aktualisieren der Pipeline');
    }
  };

  if (loading) return <div>Lade Pipelines...</div>;
  if (error) return <div>{error}</div>;

  // Styles
  const buttonStyle = { fontSize: '15px', padding: '5px 10px', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '5px' };
  const addButtonStyle = { ...buttonStyle, background: '#28a745' };
  const editButtonStyle = { ...buttonStyle, background: '#007bff' };
  const saveButtonStyle = { ...buttonStyle, background: '#28a745' };
  const cancelButtonStyle = { ...buttonStyle, background: '#6c757d' };
  const deleteButtonStyle = { ...buttonStyle, background: '#dc3545' };

  return (
    <div>
      <Helmet>
        <title>Pipelines verwalten</title>
        <meta name="description" content="CRM-Pipelines verwalten" />
      </Helmet>
      <h2>Pipelines</h2>

      {/* Formular für neue Pipeline */}
      <div style={{ display: 'flex', gap: '20px', alignItems: 'center', marginBottom: '10px', marginLeft: '280px', width: '70%' }}>
        <input
          type="text"
          value={newPipeline.name}
          onChange={(e) => setNewPipeline({ ...newPipeline, name: e.target.value })}
          placeholder="Name der Pipeline"
        />
        <input
          type="text"
          value={newPipeline.description}
          onChange={(e) => setNewPipeline({ ...newPipeline, description: e.target.value })}
          placeholder="Beschreibung"
        />
        <label>
          <input
            type="checkbox"
            checked={newPipeline.isActive}
            onChange={(e) => setNewPipeline({ ...newPipeline, isActive: e.target.checked })}
            style={{ marginRight: 5 }}
          />
          Aktiv
        </label>
        <button onClick={handleAddPipeline} style={addButtonStyle}>
          Hinzufügen
        </button>
      </div>

      {/* Tabelle mit Pipelines */}
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Beschreibung</th>
            <th>Aktiv</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {pipelines.map(p => (
            <tr key={p.id}>
              <td>{p.id}</td>
              <td>
                {editingPipeline?.id === p.id ? (
                  <input
                    type="text"
                    name="name"
                    value={editingPipeline.name}
                    onChange={handleEditInputChange}
                  />
                ) : (
                  p.name
                )}
              </td>
              <td>
                {editingPipeline?.id === p.id ? (
                  <input
                    type="text"
                    name="description"
                    value={editingPipeline.description}
                    onChange={handleEditInputChange}
                  />
                ) : (
                  p.description || '-'
                )}
              </td>
              <td>
                {editingPipeline?.id === p.id ? (
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={!!editingPipeline.isActive}
                    onChange={handleEditInputChange}
                  />
                ) : (
                  p.isActive ? '✅' : '❌'
                )}
              </td>
              <td>
                {editingPipeline?.id === p.id ? (
                  <>
                    <button onClick={handleSavePipeline} style={saveButtonStyle}>Speichern</button>
                    <button onClick={handleCancelEdit} style={cancelButtonStyle}>Abbrechen</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEditClick(p)} style={editButtonStyle}>Bearbeiten</button>
                    <button onClick={() => handleDeletePipeline(p.id)} style={deleteButtonStyle}>Löschen</button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PipelineDefinitionForm;
